import { createTheme } from '@mui/material';

/**
 * App-wide look and feel. Purely cosmetic: colors, radii, shadows and
 * typography only — no component behaviour or layout structure is changed.
 */
const baseTheme = createTheme({
  palette: {
    primary: { main: '#4f46e5' },
    background: { default: '#f5f6f8', paper: '#ffffff' },
    text: { primary: '#1e293b', secondary: '#64748b' },
    divider: '#e5e8ee',
  },
  shape: { borderRadius: 10 },
  typography: {
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
});

// Soften the default elevation-1 shadow used by Paper/Card/Accordion.
const shadows = [...baseTheme.shadows] as typeof baseTheme.shadows;
shadows[1] = '0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.07)';

export const theme = createTheme(baseTheme, {
  shadows,
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    // Compact density everywhere: most of the app already used size="small",
    // the rest now match it instead of the tall 56px default.
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#ffffff',
          transition: 'box-shadow 0.15s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d6dce6',
            transition: 'border-color 0.15s ease',
          },
          '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: '#94a3b8',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.14)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.12)',
          },
          '& input::placeholder': {
            color: '#94a3b8',
            opacity: 1,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        icon: { color: '#94a3b8' },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:not(.Mui-checked)': { color: '#94a3b8' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${baseTheme.palette.divider}`,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
          marginTop: 4,
        },
        list: {
          padding: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          borderRadius: 6,
          padding: '6px 10px',
          '&.Mui-selected': { fontWeight: 500 },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${baseTheme.palette.divider}`,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
        },
        listbox: {
          padding: 6,
        },
        option: {
          fontSize: '0.9rem',
          borderRadius: 6,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${baseTheme.palette.divider}`,
          '&:before': { display: 'none' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#f8fafc',
          },
        },
      },
    },
  },
});
